import React from 'react'
import Helmet from 'react-helmet'

import { MEAL_PLANNING_PAGE, PageRenderer, useCmsBlock } from '@library/cms-blocks'
import type { CmsCtaIntentHandlers, CmsFetcher, CmsPageFields } from '@library/cms-blocks'

import fallbackOgImage from '../../sections/assets/carouselimage.jpg'
import type { HowGoustoFoodBoxesWorkTypes } from '../../types'
import { TargetUri } from '../../types/tracking'
import { MealPlanningPage } from '../MealPlanningPage'
import { SEO } from '../MealPlanningPage/config'

type Props = HowGoustoFoodBoxesWorkTypes & {
  cmsFetcher: CmsFetcher
}

type RobotsDirective = NonNullable<CmsPageFields['robots_directive']>

const fallbackDescription = SEO.head.meta[0]?.content || ''
const defaultRobotsDirective: RobotsDirective = 'index, follow'
const pageUrl = `https://www.gousto.co.uk${MEAL_PLANNING_PAGE.pageUrl}`

function getCmsText(value: string | undefined, fallback: string) {
  const trimmedValue = value?.trim()

  return trimmedValue || fallback
}

export function MealPlanningCmsPage({ cmsFetcher, startOnboarding }: Props) {
  const { data, error } = useCmsBlock(MEAL_PLANNING_PAGE, cmsFetcher)
  const pageTitle = getCmsText(data?.seo_title, SEO.head.title)
  const pageDescription = getCmsText(data?.seo_description, fallbackDescription)
  const ogImage = getCmsText(data?.seo_og_image?.url, fallbackOgImage)
  const robotsDirective = data?.robots_directive || defaultRobotsDirective
  const createOnboardingHandler =
    (targetUri: TargetUri): React.MouseEventHandler<HTMLElement> =>
    (event) => {
      event.preventDefault()
      startOnboarding(targetUri)
    }

  const ctaIntentHandlers: CmsCtaIntentHandlers = {
    start_onboarding_wizard: {
      href: TargetUri.SIGNUP,
      onClick: createOnboardingHandler(TargetUri.SIGNUP),
    },
    start_onboarding_menu: {
      href: TargetUri.MENU,
      onClick: createOnboardingHandler(TargetUri.MENU),
    },
  }

  // New model: the CTA carries its own url; trigger_promocode opts the click
  // into the promo-code dispatch (apply promo code + redirect).
  const handleTriggerPromocode = (href: string) => {
    startOnboarding(href as TargetUri)
  }

  if (error) {
    return <MealPlanningPage startOnboarding={startOnboarding} />
  }

  return (
    <div data-testid="meal-planning-cms-page">
      <Helmet title={pageTitle} link={[{ rel: 'canonical', href: pageUrl }]}>
        <meta name="description" content={pageDescription} />
        <meta name="robots" content={robotsDirective} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:image" content={ogImage} />
      </Helmet>
      {data ? (
        <PageRenderer
          page={data}
          ctaIntentHandlers={ctaIntentHandlers}
          onTriggerPromocode={handleTriggerPromocode}
        />
      ) : null}
    </div>
  )
}
